import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Calendar, Clock, AlertTriangle, FileText, ChevronRight, Loader2 } from "lucide-react";

// ── Urgency helpers ──────────────────────────────────────────────────────────
const urgency = (days) => {
  if (days === null || days === undefined) return { label: "Sin fecha", dot: "bg-gray-300", text: "text-gray-500", badge: "bg-gray-100 text-gray-600" };
  if (days <= 7)  return { label: `${days}d`, dot: "bg-red-500 animate-pulse", text: "text-red-700 font-bold", badge: "bg-red-100 text-red-700" };
  if (days <= 14) return { label: `${days}d`, dot: "bg-orange-500", text: "text-orange-700 font-semibold", badge: "bg-orange-100 text-orange-700" };
  if (days <= 21) return { label: `${days}d`, dot: "bg-amber-400", text: "text-amber-700", badge: "bg-amber-100 text-amber-700" };
  return { label: `${days}d`, dot: "bg-green-400", text: "text-green-700", badge: "bg-green-100 text-green-700" };
};

// ── Main component ───────────────────────────────────────────────────────────
export default function UpcomingWidget({ compact = false }) {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/licitaciones/upcoming-deadlines?days_ahead=30`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="upcoming-widget-loading">
        <div className="flex items-center gap-2 mb-4">
          <Calendar size={16} className="text-[#007934]" />
          <h3 className="font-semibold text-gray-900 font-['Outfit']">Mis Upcoming</h3>
        </div>
        <div className="flex items-center justify-center py-6">
          <Loader2 size={20} className="animate-spin text-gray-300" />
        </div>
      </div>
    );
  }

  const visibleItems = compact ? items.slice(0, 3) : items.slice(0, 6);
  const staleCount = items.filter((i) => i.memoria_days_stale >= 7 || (!i.has_memoria && i.days_remaining <= 21)).length;

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="upcoming-widget">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-[#007934]" />
          <h3 className="font-semibold text-gray-900 font-['Outfit']">Mis Upcoming</h3>
          {staleCount > 0 && (
            <span
              className="flex items-center gap-1 text-[10px] font-bold bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full"
              data-testid="stale-count-badge"
              title="Licitaciones con Memoria Técnica sin actualizar"
            >
              <AlertTriangle size={9} /> {staleCount}
            </span>
          )}
        </div>
        <button
          onClick={() => navigate("/licitaciones")}
          className="text-xs text-[#007934] flex items-center gap-1 hover:underline"
          data-testid="upcoming-see-all-btn"
        >
          Ver CRM <ChevronRight size={12} />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 text-center">
          <Calendar size={28} className="text-gray-200 mb-2" />
          <p className="text-sm text-gray-500">Sin presentaciones en los próximos 30 días</p>
        </div>
      ) : (
        <div className="space-y-2">
          {visibleItems.map((item) => {
            const u = urgency(item.days_remaining);
            const memStale = item.memoria_days_stale >= 7 || (!item.has_memoria && item.days_remaining <= 21);
            return (
              <div
                key={item.id}
                onClick={() => navigate(`/licitaciones/${item.id}`)}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-gray-50 cursor-pointer transition-all group"
                data-testid={`upcoming-item-${item.id}`}
              >
                {/* urgency dot */}
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${u.dot}`} />

                {/* info */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#007934]">
                    {item.titulo}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {item.municipio} · {item.tipo_servicio || "Licitación"}
                  </p>
                </div>

                {/* right side */}
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {memStale && (
                    <span
                      className="flex items-center gap-0.5 text-[10px] font-semibold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full"
                      title="Memoria Técnica sin actualizar"
                      data-testid={`stale-mem-badge-${item.id}`}
                    >
                      <FileText size={9} />
                      {item.has_memoria ? `${item.memoria_days_stale}d` : "Sin mem."}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${u.badge}`}>
                    {u.label}
                  </span>
                </div>
              </div>
            );
          })}

          {items.length > visibleItems.length && (
            <button
              onClick={() => navigate("/licitaciones")}
              className="w-full text-center text-xs text-gray-400 hover:text-[#007934] py-1 transition-colors"
            >
              + {items.length - visibleItems.length} más →
            </button>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="mt-3 pt-3 border-t border-gray-50 flex flex-wrap gap-3 text-[10px] text-gray-400">
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" />≤7 días</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-orange-500 inline-block" />≤14 días</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400 inline-block" />≤21 días</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400 inline-block" />&gt;21 días</span>
        <span className="flex items-center gap-1 ml-auto text-amber-600"><FileText size={9} />Memoria sin actualizar</span>
      </div>
    </div>
  );
}
